/**
 * Bestimmen statt suchen.
 *
 * Wer nachts auf dem Wasser ein Fahrzeug sieht, kennt seinen Namen nicht. Genau deshalb
 * schlägt er nach. Eine Suche nach "Schleppverband" hilft ihm nicht: das Wort ist die
 * Antwort, die er sucht, nicht die Frage, die er stellen kann. Was er hat, sind Merkmale:
 * zwei weiße übereinander, ein rotes daneben, und irgendwo blinkt etwas.
 *
 * Feldführer für Vögel und Pflanzen lösen das seit hundert Jahren mit einem
 * Bestimmungsschlüssel: beobachtbare Merkmale antippen, die Treffermenge schrumpft. Das
 * ist die Form, die hierher gehört, und sie ist der Grund, warum diese App keine
 * Karteikartensammlung ist.
 *
 * Zwei Entscheidungen prägen die Auswahl:
 *
 * 1. **Angegebene Farben müssen vorkommen, nicht genau die Menge sein.** Sektoren
 *    verdecken Lichter: wer querab steht, sieht ein Seitenlicht und das Topplicht, das
 *    zweite Seitenlicht nie. Wer "weiß und rot" wählt, meint "ich sehe weiß und rot",
 *    nicht "ich sehe nichts anderes".
 * 2. **Zahlen gelten als Untergrenze.** Drei weiße Lichter sind auch dann drei, wenn ein
 *    viertes hinter dem Aufbau steht. Wer zwei wählt, schließt das Bild mit dreien nicht
 *    aus; wer drei wählt, schließt das mit zweien aus.
 *
 * Beides macht die Auswahl großzügig in die richtige Richtung: sie verliert den richtigen
 * Treffer nicht, sie zeigt eher einen zu viel. Ein Bestimmungsschlüssel, der die Antwort
 * wegfiltert, ist schlimmer als einer, der drei Vorschläge macht.
 */
import { binnenStates, lightStates } from './data'
import type { BinnenLightStateData, LightStateData } from './schema'
import type { Bereich } from './settings'
import type { DayShapeKind, LightColour, LightSpec } from './types'

/** Die Farben in der Reihenfolge, in der die Oberfläche sie anbietet. */
export const FARBEN: LightColour[] = ['white', 'red', 'green', 'yellow', 'blue']

export interface Merkmale {
  /** Welche Farben brennen. Leer heißt: keine Einschränkung. */
  farben: LightColour[]
  /** Wie viele weiße Lichter. 0 heißt "keins", 3 heißt "drei oder mehr". */
  weisse?: 0 | 1 | 2 | 3
  /** Stehen zwei Lichter senkrecht übereinander? */
  uebereinander?: boolean
  /**
   * Welche Farbe über welcher, von oben nach unten.
   *
   * Das Merkmal, an dem die Seefahrt ihre Merksprüche aufhängt: rot über weiß, rot über
   * rot, grün über weiß. Gesucht wird als zusammenhängendes Stück, nicht als ganze Folge,
   * denn wer drei Lichter übereinander sieht, sieht je nach Stand auch nur zwei davon.
   *
   * Die leere Folge ist die Antwort "nichts übereinander", und sie ist so viel wert wie
   * jede andere: sie trennt den halben Bestand ab. Zwei Topplichter vorn und achtern
   * zählen dabei nicht, die stehen an verschiedenen Stellen und sind über ihre Zahl
   * beschrieben.
   */
  folge?: LightColour[]
  /** Wie viele Topplichter. 0 heißt "keines", darüber ist es eine Untergrenze. */
  topp?: 0 | 1 | 2 | 3
  /** Wie viele Rundumlichter. 0 heißt "keines", darüber ist es eine Untergrenze. */
  rundum?: 0 | 1 | 2 | 3
  /** Ist ein Seitenlicht zu sehen? `false` heißt: keines. */
  seitenlicht?: boolean
  /** Blinkt oder funkelt eines? `false` heißt: keines. */
  blinkt?: boolean
  /** Bei Tag: steht ein Signalkörper am Mast? */
  tagzeichen?: boolean
  /** Welche Form hat der Signalkörper? */
  tagform?: DayShapeKind
  /**
   * "Ich sehe das Fahrzeug frei und ganz."
   *
   * Die zweite Achse des Schlüssels, und die ehrlichste Stelle der ganzen App: nicht die
   * Merkmale entscheiden über die Schärfe, sondern wie sicher der Beobachter sich ist.
   *
   * Ohne diese Angabe zählt alles als Untergrenze. Wer weiß und rot wählt, schließt ein
   * Bild mit zusätzlichem Grün nicht aus, denn aus einer schrägen Peilung verdeckt der
   * Sektor das grüne Seitenlicht; wer zwei weiße zählt, schließt das Bild mit dreien nicht
   * aus. Gemessen über alle Bilder aus zwölf Peilungen verliert der Schlüssel so **nie**
   * den richtigen Treffer, und das kostet ihn: im Mittel bleiben 12,3 Vorschläge stehen.
   *
   * Mit der Angabe zählen die Farben als ganze Menge und die Zahlen als genaue Zahlen,
   * und aus 37 Bildern wird im Mittel eines. Wer sie setzt, ohne freie Sicht zu haben,
   * filtert sich seinen Treffer weg. Deshalb heißt sie, was sie ist, und steht nicht
   * voreingestellt an.
   *
   * Die Fragen tragen ihre Null davon unabhängig: "keine Topplichter" ist eine
   * Beobachtung, "ich habe nicht darauf geachtet" ist keine, und die Null gilt in beiden
   * Fällen genau.
   */
  sichtFrei?: boolean
}

export interface Treffer {
  id: string
  titel: { de: string; en: string }
  bereich: Bereich
  /** Die Farben des Bildes, in der Reihenfolge von FARBEN. */
  farben: LightColour[]
  /** Fundstelle, kurz: "Rule 23" oder "§ 3.08". */
  fundstelle: string
  /**
   * Wo das Bild gilt. Vier Zustände des Seebestands stehen nur in den internationalen
   * Regeln und kommen unter den US-Binnenregeln gar nicht vor; wer die Liste durchgeht,
   * muss das am Eintrag sehen und nicht erst im Fließtext darunter.
   */
  geltung?: 'international' | 'inland'
}

type Bild = LightStateData | BinnenLightStateData

export const LEER: Merkmale = { farben: [] }

/** Wie viele Merkmale gesetzt sind. Null heißt: der Schlüssel zeigt alles. */
export function anzahlMerkmale(merkmale: Merkmale): number {
  return (
    merkmale.farben.length +
    (merkmale.sichtFrei ? 1 : 0) +
    (merkmale.weisse !== undefined ? 1 : 0) +
    (merkmale.uebereinander ? 1 : 0) +
    (merkmale.folge ? 1 : 0) +
    (merkmale.topp !== undefined ? 1 : 0) +
    (merkmale.rundum !== undefined ? 1 : 0) +
    (merkmale.seitenlicht !== undefined ? 1 : 0) +
    (merkmale.blinkt !== undefined ? 1 : 0) +
    (merkmale.tagzeichen ? 1 : 0) +
    (merkmale.tagform ? 1 : 0)
  )
}

export function farbenVon(lights: LightSpec[]): LightColour[] {
  const da = new Set(lights.map((l) => l.colour))
  return FARBEN.filter((f) => da.has(f))
}

function weisseZahl(lights: LightSpec[]): number {
  return lights.filter((l) => l.colour === 'white').length
}

/**
 * Zwei Lichter senkrecht übereinander.
 *
 * Gemeint ist, was man von der Seite sieht: zwei Lichter an derselben Stelle des
 * Fahrzeugs in verschiedener Höhe. Die Ordnung schreibt das als Staffel (`tier`), und
 * genau daran ist es zu erkennen. Seitenlichter zählen nicht: die stehen nebeneinander,
 * und sie hat jedes Fahrzeug, also würden sie nichts unterscheiden.
 */
function hatUebereinander(lights: LightSpec[]): boolean {
  const proStelle = new Map<string, Set<number>>()
  for (const licht of lights) {
    if (licht.type === 'sidelight-port' || licht.type === 'sidelight-stbd') continue
    const stelle = `${licht.mount}:${licht.versatzM ?? 0}`
    const hoehen = proStelle.get(stelle) ?? new Set<number>()
    hoehen.add(licht.tier ?? 0)
    proStelle.set(stelle, hoehen)
  }
  for (const hoehen of proStelle.values()) if (hoehen.size > 1) return true
  // Topplicht vorn und achtern gestaffelt: die klassische Zwei-Topplichter-Lage.
  const topp = lights.filter((l) => l.type === 'masthead')
  return topp.length > 1
}

/**
 * Die senkrechten Folgen eines Bildes, jede von oben nach unten.
 *
 * Gelesen wird die Staffel aus den Daten: Lichter an derselben Stelle (`mount` und
 * seitlicher Versatz) in verschiedener Höhe (`tier`). Seitenlichter bleiben außen vor, die
 * stehen nebeneinander und hat jedes fahrende Fahrzeug.
 *
 * Ein Fahrzeug kann mehrere Folgen führen: der Bagger trägt zwei grüne an der einen Seite,
 * zwei rote an der anderen und rot-weiß-rot am Mast, und jede davon ist ein eigenes
 * Erkennungszeichen.
 */
export function folgenVon(lights: LightSpec[]): LightColour[][] {
  const proStelle = new Map<string, LightSpec[]>()
  for (const licht of lights) {
    if (licht.type === 'sidelight-port' || licht.type === 'sidelight-stbd') continue
    if (licht.type === 'sidelights-combined') continue
    const stelle = `${licht.mount}:${licht.versatzM ?? 0}`
    proStelle.set(stelle, [...(proStelle.get(stelle) ?? []), licht])
  }
  const raus: LightColour[][] = []
  for (const liste of proStelle.values()) {
    if (liste.length < 2) continue
    const hoehen = new Set(liste.map((l) => l.tier ?? 0))
    if (hoehen.size < 2) continue
    const sortiert = [...liste].sort((a, b) => (b.tier ?? 0) - (a.tier ?? 0))
    raus.push(sortiert.map((l) => l.colour))
  }
  return raus
}

/**
 * Steht die gesuchte Folge so am Fahrzeug?
 *
 * Gesucht wird als zusammenhängendes Stück einer Folge, nicht als ganze Folge. Wer bei
 * einem Bagger rot über weiß sieht, hat recht, auch wenn dort in Wahrheit rot über weiß
 * über rot steht: das dritte Licht kann hinter dem Aufbau stehen oder außerhalb seiner
 * Tragweite liegen. Der Schlüssel darf die Antwort nicht wegfiltern, nur weil der
 * Beobachter weniger sieht als da ist.
 */
export function hatFolge(lights: LightSpec[], gesucht: LightColour[]): boolean {
  if (gesucht.length < 2) return true
  for (const folge of folgenVon(lights)) {
    for (let i = 0; i + gesucht.length <= folge.length; i += 1) {
      if (gesucht.every((farbe, k) => folge[i + k] === farbe)) return true
    }
  }
  return false
}

function toppZahl(lights: LightSpec[]): number {
  return lights.filter((l) => l.type === 'masthead').length
}

function rundumZahl(lights: LightSpec[]): number {
  return lights.filter((l) => l.type === 'allround').length
}

function hatSeitenlicht(lights: LightSpec[]): boolean {
  return lights.some((l) => l.type.startsWith('sidelight'))
}

function hatBlinken(lights: LightSpec[]): boolean {
  return lights.some((l) => l.rhythm === 'flashing' || l.rhythm === 'special-flashing')
}

function fundstelleVon(bild: Bild): string {
  return 'rule' in bild ? `Rule ${bild.rule}` : `§ ${bild.paragraph}`
}

/**
 * Passt eine gezählte Zahl zur Angabe?
 *
 * Die Null ist eine Beobachtung und gilt genau: wer sagt, er sehe kein Topplicht, sieht
 * keines. Jede Zahl darüber ist eine Untergrenze, denn ein weiteres Licht kann hinter dem
 * Aufbau stehen oder außerhalb seiner Tragweite liegen. So verliert der Schlüssel den
 * richtigen Treffer nie, und die Null trägt trotzdem ihre volle Schärfe.
 */
function zahlPasst(gezaehlt: number, gewaehlt: number, sichtFrei: boolean): boolean {
  if (gewaehlt === 0) return gezaehlt === 0
  // Die oberste Stufe heißt "drei oder mehr" und bleibt eine Untergrenze, auch bei freier
  // Sicht: die Oberfläche zählt nicht weiter, also kann die Angabe nicht genauer sein.
  if (!sichtFrei || gewaehlt >= 3) return gezaehlt >= gewaehlt
  return gezaehlt === gewaehlt
}

/** Passt ein Bild zu den Merkmalen? */
export function passt(bild: Bild, merkmale: Merkmale): boolean {
  const farben = farbenVon(bild.lights)
  for (const farbe of merkmale.farben) {
    if (!farben.includes(farbe)) return false
  }
  const sichtFrei = merkmale.sichtFrei === true
  // Bei freier Sicht sind die genannten Farben alle Farben.
  if (sichtFrei && merkmale.farben.length > 0 && farben.some((f) => !merkmale.farben.includes(f))) {
    return false
  }
  if (merkmale.weisse !== undefined && !zahlPasst(weisseZahl(bild.lights), merkmale.weisse, sichtFrei)) {
    return false
  }
  if (merkmale.uebereinander && !hatUebereinander(bild.lights)) return false
  if (merkmale.folge && merkmale.folge.length === 0) {
    // "Nichts übereinander": dann darf das Bild an keiner Stelle eine Staffel führen.
    if (folgenVon(bild.lights).length > 0) return false
  } else if (merkmale.folge) {
    // Bei freier Sicht ist die genannte Folge die ganze Folge, sonst ein Stück davon.
    const passtFolge = sichtFrei
      ? folgenVon(bild.lights).some(
          (f) => f.length === merkmale.folge!.length && f.every((x, i) => x === merkmale.folge![i]),
        )
      : hatFolge(bild.lights, merkmale.folge)
    if (!passtFolge) return false
  }
  if (merkmale.topp !== undefined && !zahlPasst(toppZahl(bild.lights), merkmale.topp, sichtFrei)) return false
  if (merkmale.rundum !== undefined && !zahlPasst(rundumZahl(bild.lights), merkmale.rundum, sichtFrei)) {
    return false
  }
  if (merkmale.seitenlicht !== undefined && hatSeitenlicht(bild.lights) !== merkmale.seitenlicht) return false
  if (merkmale.blinkt !== undefined && hatBlinken(bild.lights) !== merkmale.blinkt) return false
  if (merkmale.tagzeichen && bild.dayShapes.length === 0) return false
  if (merkmale.tagform && !bild.dayShapes.some((d) => d.kind === merkmale.tagform)) return false
  return true
}

/**
 * Die Treffer zu den Merkmalen, im gewählten Bereich.
 *
 * Sortiert wird nach Sparsamkeit: das Bild mit den wenigsten Lichtern zuerst. Wer drei
 * Lichter sieht und drei Vorschläge bekommt, prüft den einfachsten zuerst, und meistens
 * stimmt er.
 */
export function bestimme(merkmale: Merkmale, bereich: Bereich): Treffer[] {
  const bestand: Bild[] = bereich === 'binnen' ? binnenStates : lightStates
  return bestand
    .filter((bild) => passt(bild, merkmale))
    .sort((a, b) => a.lights.length - b.lights.length || a.id.localeCompare(b.id))
    .map((bild) => ({
      id: bild.id,
      titel: bild.title,
      bereich,
      farben: farbenVon(bild.lights),
      fundstelle: fundstelleVon(bild),
      ...('scope' in bild && bild.scope !== 'both' ? { geltung: bild.scope } : {}),
    }))
}

/**
 * Welche Merkmale die Treffermenge noch teilen.
 *
 * Ein Bestimmungsschlüssel ist nur so gut wie seine nächste Frage. Ein Merkmal, das alle
 * verbliebenen Bilder haben oder keines, bringt nichts mehr; die Oberfläche blendet es
 * aus, statt den Nutzer raten zu lassen, was ihn weiterbringt.
 */
export function hilftNoch(merkmale: Merkmale, bereich: Bereich): {
  farben: LightColour[]
  weisse: boolean
  uebereinander: boolean
  blinkt: boolean
  tagzeichen: boolean
} {
  const bestand: Bild[] = (bereich === 'binnen' ? binnenStates : lightStates).filter((bild) =>
    passt(bild, merkmale),
  )
  const teilt = (pruefe: (bild: Bild) => boolean): boolean => {
    const ja = bestand.filter(pruefe).length
    return ja > 0 && ja < bestand.length
  }
  return {
    farben: FARBEN.filter(
      (farbe) => !merkmale.farben.includes(farbe) && teilt((bild) => farbenVon(bild.lights).includes(farbe)),
    ),
    weisse: teilt((bild) => weisseZahl(bild.lights) >= 2),
    uebereinander: !merkmale.uebereinander && teilt((bild) => hatUebereinander(bild.lights)),
    blinkt: !merkmale.blinkt && teilt((bild) => hatBlinken(bild.lights)),
    tagzeichen: !merkmale.tagzeichen && teilt((bild) => bild.dayShapes.length > 0),
  }
}

/**
 * Die Fragen des Schlüssels, sortiert nach dem, was sie bringen.
 *
 * Ein Bestimmungsschlüssel ist nur so gut wie seine nächste Frage. Gemessen wird sie an
 * der Treffermenge, die nach der Antwort zu erwarten ist: die Frage teilt den Bestand in
 * Gruppen, und wer eine Gruppe der Größe n trifft, hat danach n Bilder vor sich. Über alle
 * Gruppen gewichtet ergibt das Summe(n²) geteilt durch N, und die kleinste Zahl gewinnt.
 *
 * Das ist derselbe Gedanke wie beim Zwanzig-Fragen-Spiel: gefragt wird, was die Menge
 * halbiert, nicht was gerade einfällt. Eine Frage, die alle verbliebenen Bilder gleich
 * beantworten, teilt nichts mehr und fällt heraus, statt den Nutzer raten zu lassen.
 */
export type FrageArt =
  | 'farben'
  | 'weisse'
  | 'folge'
  | 'topp'
  | 'rundum'
  | 'seitenlicht'
  | 'blinkt'
  | 'uebereinander'
  | 'tagzeichen'
  | 'tagform'

/**
 * Die Antwort für den, der es nicht genau sagen kann.
 *
 * Nachts auf See sind zwei Lichter übereinander oft erkennbar, ihre Reihenfolge aber
 * nicht: auf zwei Seemeilen liegen sie wenige Bogenminuten auseinander, und bei Seegang
 * tanzt das Bild. Wer dann raten muss, ob rot über weiß oder weiß über rot steht, rät mit
 * fünfzig Prozent falsch und filtert sich seinen eigenen Treffer weg, denn die Folge
 * prüft die Reihenfolge scharf.
 *
 * Deshalb steht neben den scharfen Antworten immer die grobe. Sie behauptet weniger und
 * hält mehr Bilder, und genau das ist in dieser Lage richtig.
 */
export const UNBEKANNT = 'unbekannt'

export interface Frage {
  art: FrageArt
  /** Die Werte, die die Oberfläche unter dieser Frage anbietet. */
  werte: (LightColour | LightColour[] | number | DayShapeKind | typeof UNBEKANNT)[]
  /** Erwartete Treffermenge nach der Antwort. Kleiner ist besser. */
  rest: number
}

/** Erwartete Treffermenge, wenn der Bestand nach `klasse` zerfällt. */
function erwarteteMenge(bestand: Bild[], klasse: (bild: Bild) => string): number {
  const gruppen = new Map<string, number>()
  for (const bild of bestand) {
    const k = klasse(bild)
    gruppen.set(k, (gruppen.get(k) ?? 0) + 1)
  }
  let summe = 0
  for (const n of gruppen.values()) summe += n * n
  return summe / bestand.length
}

function folgeSchluessel(folge: LightColour[]): string {
  return folge.join('-')
}

/**
 * Die Folgen, die im Bestand tatsächlich vorkommen, von der häufigsten zur seltensten.
 *
 * Angeboten wird nur, was es gibt: eine Liste aller denkbaren Farbpaare wäre dreißig
 * Einträge lang und die meisten davon führten ins Leere.
 */
export function folgenImBestand(bestand: Bild[]): LightColour[][] {
  const gesehen = new Map<string, { folge: LightColour[]; zahl: number }>()
  for (const bild of bestand) {
    for (const folge of folgenVon(bild.lights)) {
      const k = folgeSchluessel(folge)
      const alt = gesehen.get(k)
      if (alt) alt.zahl += 1
      else gesehen.set(k, { folge, zahl: 1 })
    }
  }
  return [...gesehen.values()].sort((a, b) => b.zahl - a.zahl || a.folge.length - b.folge.length).map((x) => x.folge)
}

/**
 * Was eine Antwort am Merkmalssatz ändert.
 *
 * Steht hier und nicht nur in der Oberfläche, weil `fragen` selbst damit rechnet: eine
 * Antwort, die auf null Treffer führt, ist eine Sackgasse und wird gar nicht erst
 * angeboten.
 */
export function mitAntwort(
  merkmale: Merkmale,
  art: FrageArt,
  wert: Frage['werte'][number],
): Merkmale {
  switch (art) {
    case 'farben':
      return { ...merkmale, farben: [...merkmale.farben, wert as LightColour] }
    case 'folge':
      // Die grobe Antwort setzt das grobe Merkmal, nicht eine geratene Reihenfolge.
      return wert === UNBEKANNT
        ? { ...merkmale, uebereinander: true }
        : { ...merkmale, folge: wert as LightColour[] }
    case 'seitenlicht':
      return { ...merkmale, seitenlicht: wert === 1 }
    case 'blinkt':
      return { ...merkmale, blinkt: wert === 1 }
    case 'tagform':
      return wert === UNBEKANNT
        ? { ...merkmale, tagzeichen: true }
        : { ...merkmale, tagform: wert as DayShapeKind }
    case 'weisse':
    case 'topp':
    case 'rundum':
      return { ...merkmale, [art]: wert as 0 | 1 | 2 | 3 }
    default:
      return merkmale
  }
}

export function fragen(merkmale: Merkmale, bereich: Bereich, tagModus = false): Frage[] {
  const bestand: Bild[] = (bereich === 'binnen' ? binnenStates : lightStates).filter((bild) =>
    passt(bild, merkmale),
  )
  if (bestand.length < 2) return []

  const roh: Frage[] = []
  const teilt = (frage: Frage): boolean => frage.rest < bestand.length

  const offeneFarben = FARBEN.filter(
    (farbe) =>
      !merkmale.farben.includes(farbe) &&
      bestand.some((b) => farbenVon(b.lights).includes(farbe)) &&
      !bestand.every((b) => farbenVon(b.lights).includes(farbe)),
  )
  if (offeneFarben.length > 0) {
    roh.push({
      art: 'farben',
      werte: offeneFarben,
      rest: erwarteteMenge(bestand, (b) => offeneFarben.map((f) => (farbenVon(b.lights).includes(f) ? '1' : '0')).join('')),
    })
  }

  if (merkmale.weisse === undefined) {
    roh.push({
      art: 'weisse',
      werte: [0, 1, 2, 3],
      rest: erwarteteMenge(bestand, (b) => String(Math.min(3, weisseZahl(b.lights)))),
    })
  }

  if (!merkmale.folge && !merkmale.uebereinander) {
    const folgen = folgenImBestand(bestand)
    if (folgen.length > 0) {
      roh.push({
        art: 'folge',
        /**
         * Drei Arten von Antwort, von grob nach fein: nichts übereinander, etwas
         * übereinander ohne Reihenfolge, und die genaue Folge. Die beiden groben stehen
         * vorn, weil sie das sind, was ein Beobachter nachts sicher sagen kann.
         */
        werte: [[], UNBEKANNT, ...folgen],
        rest: erwarteteMenge(bestand, (b) =>
          folgenVon(b.lights).map(folgeSchluessel).sort().join('+') || '-',
        ),
      })
    }
  }

  if (merkmale.topp === undefined) {
    roh.push({
      art: 'topp',
      werte: [0, 1, 2, 3],
      rest: erwarteteMenge(bestand, (b) => String(Math.min(3, toppZahl(b.lights)))),
    })
  }

  if (merkmale.rundum === undefined) {
    roh.push({
      art: 'rundum',
      werte: [0, 1, 2, 3],
      rest: erwarteteMenge(bestand, (b) => String(Math.min(3, rundumZahl(b.lights)))),
    })
  }

  if (merkmale.seitenlicht === undefined) {
    roh.push({
      art: 'seitenlicht',
      werte: [0, 1],
      rest: erwarteteMenge(bestand, (b) => (hatSeitenlicht(b.lights) ? '1' : '0')),
    })
  }

  if (merkmale.blinkt === undefined) {
    roh.push({
      art: 'blinkt',
      werte: [0, 1],
      rest: erwarteteMenge(bestand, (b) => (hatBlinken(b.lights) ? '1' : '0')),
    })
  }

  if (tagModus) {
    const formen = [...new Set(bestand.flatMap((b) => b.dayShapes.map((d) => d.kind)))]
    if (!merkmale.tagform && !merkmale.tagzeichen && formen.length > 0) {
      roh.push({
        art: 'tagform',
        // Auch hier die grobe Antwort: ein Signalkörper am Mast ist auf Entfernung als
        // Fleck zu sehen, lange bevor seine Form zu erkennen ist.
        werte: [UNBEKANNT, ...formen],
        rest: erwarteteMenge(bestand, (b) => b.dayShapes.map((d) => d.kind).sort().join(',') || '-'),
      })
    }
  }

  /**
   * Keine Sackgassen anbieten.
   *
   * "Weiß über rot" und dazu "keine weißen Lichter" ist ein Widerspruch, und die
   * Oberfläche bot ihn an: ein Tipp, und die Liste war leer. Ein Bestimmungsschlüssel,
   * der eine Antwort zeigt, hinter der nichts steht, schickt den Nutzer in die Irre und
   * lässt ihn selbst herausfinden, welcher seiner Tipps schuld war.
   */
  const ohneSackgassen = roh
    .map((frage) => ({
      ...frage,
      werte: frage.werte.filter((wert) =>
        bestand.some((bild) => passt(bild, mitAntwort(merkmale, frage.art, wert))),
      ),
    }))
    .filter((frage) => frage.werte.length > 0)

  return ohneSackgassen.filter(teilt).sort((a, b) => a.rest - b.rest || a.art.localeCompare(b.art))
}

/**
 * Die Bilder, die am selben Lichterbild hängen bleiben.
 *
 * Manche Fahrzeuge führen dieselben Lichter, und dann ist der Schlüssel nicht schlecht,
 * sondern die Ordnung eindeutig: ein Schubverband in fester Verbindung gilt nach Regel
 * 24(b) als Maschinenfahrzeug, ein Fischer ohne Fanggerät führt dessen Lichter, und das
 * Wasserflugzeug führt sie, soweit es geht. Die App sagt das, statt eine Eindeutigkeit
 * vorzutäuschen, die es nicht gibt.
 */
export function nichtWeiterTeilbar(merkmale: Merkmale, bereich: Bereich, tagModus = false): boolean {
  const treffer = bestimme(merkmale, bereich)
  return treffer.length > 1 && fragen(merkmale, bereich, tagModus).length === 0
}
